import ApiService from 'services/ApiService/ApiService';
import LocalStorageService from 'services/LocalStorageService';
import MessagingService from 'services/MessagingService/MessagingService';
import { GET_GIFS_RESPONSE, GET_GIFS_RESPONSE_STATUS } from 'services/MessagingService/EventNames';
import APIStates from 'enum/APIStates';
import { HOUR_AS_MILLISECONDS } from 'enum/timeFormat';
import parseCatch from 'utils/parseCatch';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';


const CAM4_GIPHY_ANALYTICS_ID = 'CAM4_GIPHY_ANALYTICS_ID';
const GIPHY_API_KEY = 'hhcqt8JMgAOftdLhJrAQIjzUVtuASliE';
const GIPHY_BASE_URL = 'https://api.giphy.com/v1/gifs';
const GIPHY_LIMIT = 20;
const GIPHY_SEARCH_URL = `${GIPHY_BASE_URL}/search`;
const GIPHY_TRENDING_URL = `${GIPHY_BASE_URL}/trending`;

/**
 * @class handles business logic for giphy gifs and expose methods
 */
class GiphyService {
  constructor(messagingService) {
    this.messagingService = messagingService();

    this.messagingService = messagingService();
    this.banList = [];
    this.loadBannedList();
  }

  /**
   * Method for fetching trending gifs
   * @param {number} offset - first value offset for fetching endpoint
   * @param {isLoadMore} bool - second value for showing the loading spinner
   * @emits GET_GIFS_RESPONSE emits the result data for gifs
   * @emits GET_GIFS_RESPONSE_STATUS changes the status of the api call
   */

  getTrendingGifs = (offset, isLoadMore) => {
    const queryParams = {
      'api_key': GIPHY_API_KEY,
      offset,
      limit: GIPHY_LIMIT,
      'random_id': CAM4_GIPHY_ANALYTICS_ID,
    };
    const trendingGifsCached = JSON.parse(LocalStorageService.get('trendingGifs'));
    if (!isLoadMore) {
      this.messagingService.emit(GET_GIFS_RESPONSE_STATUS, APIStates.PENDING);
    }

    if (trendingGifsCached?.expiry < new Date().getTime() - HOUR_AS_MILLISECONDS) {
      LocalStorageService.remove('trendingGifs');
    }

    const offsetLimit = 20;
    const shouldLoadNewGifs = !LocalStorageService.has('trendingGifs') || offset >= offsetLimit;
    if (shouldLoadNewGifs) {
      ApiService.get(GIPHY_TRENDING_URL, { params: queryParams, withCredentials: false })
        .then((res) => {
          this.messagingService.emit(GET_GIFS_RESPONSE, {
            gifs: res.data.data,
            isLoadMore,
          });

          if (!LocalStorageService.has('trendingGifs')) {
            const trendingGifs = {
              value: res.data.data,
              expiry: new Date().getTime(),
            };
            LocalStorageService.set('trendingGifs', JSON.stringify(trendingGifs));
          }

          if (!isLoadMore) {
            this.messagingService.emit(GET_GIFS_RESPONSE_STATUS, APIStates.LOADED);
          }
        })
        .catch((err) => {
          if (!isLoadMore) {
            this.messagingService.emit(GET_GIFS_RESPONSE_STATUS, APIStates.ERROR);
          }
          parseCatch(err);
        });
    } else {
      this.messagingService.emit(GET_GIFS_RESPONSE, { gifs: trendingGifsCached.value });
      if (!isLoadMore) {
        this.messagingService.emit(GET_GIFS_RESPONSE_STATUS, APIStates.LOADED);
      }
    }
  };

  /**
   * Method for fetching search gifs
   * @param {string} searchValue - search value parameter
   * @param {number} offset - second value offset for fetching endpoint
   * @param {isLoadMore} bool - third value for showing the loading spinner
   * @emits GET_GIFS_RESPONSE emits the result data for gifs
   * @emits GET_GIFS_RESPONSE_STATUS changes the status of the api call
   */

  getSearchGifs = (searchValue, offset, isLoadMore) => {
    if (this.isBanned(searchValue)) {
      this.getTrendingGifs(offset, isLoadMore);

      return;
    }

    const queryParams = {
      'api_key': GIPHY_API_KEY,
      q: searchValue,
      offset,
      limit: GIPHY_LIMIT,
      'random_id': CAM4_GIPHY_ANALYTICS_ID,
    };

    if (!isLoadMore) {
      this.messagingService.emit(GET_GIFS_RESPONSE_STATUS, APIStates.PENDING);
    }

    ApiService.get(GIPHY_SEARCH_URL, { params: queryParams, withCredentials: false })
      .then((res) => {
        this.messagingService.emit(GET_GIFS_RESPONSE, {
          gifs: res.data.data,
          isLoadMore,
        });
        if (!isLoadMore) {
          this.messagingService.emit(GET_GIFS_RESPONSE_STATUS, APIStates.LOADED);
        }
      })
      .catch((err) => {
        if (!isLoadMore) {
          this.messagingService.emit(GET_GIFS_RESPONSE_STATUS, APIStates.ERROR);
        }
        parseCatch(err);
      });
  };

  /**
   * Method for fetching single gifs
   * @param {string} id - id of the gif that is fetched
   */

  getSingleGif = (id) => {
    const URL = encodeURI(`${GIPHY_BASE_URL}/${id}`);
    const queryParams = { 'api_key': GIPHY_API_KEY };

    return ApiService.get(URL, { params: queryParams, withCredentials: false })
      .then((res) => res.data)
      .catch((err) => parseCatch(err));
  };

  /**
   * Method for pinging giphy server
   * @param {string} url - endpoint url for giphy server
   */
  sendAnalytics = (url) => {
    const queryParams = {
      'random_id': CAM4_GIPHY_ANALYTICS_ID,
      ts: new Date().valueOf(),
    };

    ApiService.get(url, { params: queryParams, withCredentials: false });
  };

  isBanned = (value) => this.banList.some((regExp) => regExp.test(value));

  loadBannedList = () => {
    const baseUrl = ConfigurationService.get(ConfigurationKeys.BASE_URL);
    const banListUrl = `${baseUrl}/rest/v1.0/giphy/banned-item`;

    ApiService.get(banListUrl, { withCredentials: false })
      .then((res) => {
        res.data.forEach((regExpValue) => {
          this.banList.push(new RegExp(regExpValue.toString(), 'm'));
        });

      })
      .catch((err) => parseCatch(err));
  };

}

export default new GiphyService(MessagingService);
